import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { type FieldPathByValue, type FieldValues, type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type DateParts, getMonthNames } from '@/utils/date';

type InstitutionFieldsetDateProps<T extends FieldValues> = {
  legend: string;
  name: FieldPathByValue<T, DateParts>;
};

const DATE_PART_KEYS = ['day', 'month', 'year'] satisfies Array<keyof DateParts>;

const InstitutionFieldsetDate = <T extends FieldValues>({ legend, name }: InstitutionFieldsetDateProps<T>) => {
  const form = useFormContext<T>();
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name={name as Path<T>}
      render={({ fieldState: { error } }) => {
        const rootError = error?.message;
        const hasFieldErrors = error && Object.keys(error).some((key) => (DATE_PART_KEYS as string[]).includes(key));

        return (
          <FormItem>
            <FormControl>
              <fieldset>
                <legend>{legend}*</legend>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name={`${name}.day` as Path<T>}
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <FormLabel className="sr-only">{t('fundamentals.date.day')}</FormLabel>
                        <FormControl>
                          <Input {...field} className="no-spinner" placeholder={t('fundamentals.date.day')} type="number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`${name}.month` as Path<T>}
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <FormLabel className="sr-only">{t('fundamentals.date.month')}</FormLabel>
                        <Select defaultValue={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger value={field.value}>
                              <SelectValue placeholder={t('fundamentals.date.month')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {getMonthNames().map((month, index) => (
                              <SelectItem key={month} value={(index + 1).toString()}>
                                {month}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`${name}.year` as Path<T>}
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <FormLabel className="sr-only">{t('fundamentals.date.year')}</FormLabel>
                        <FormControl>
                          <Input {...field} className="no-spinner" placeholder={t('fundamentals.date.year')} type="number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </fieldset>
            </FormControl>
            {rootError && !hasFieldErrors ? <FormMessage>{rootError}</FormMessage> : null}
          </FormItem>
        );
      }}
    />
  );
};
InstitutionFieldsetDate.displayName = 'InstitutionFieldsetDate';

export { InstitutionFieldsetDate };
